/**
 * TitleScreen - The main menu screen
 * Displays game title with glow effect, animated starfield background,
 * and menu options: Start Game, High Scores, Settings.
 */

export class TitleScreen {
  constructor(game) {
    this.game = game;
    this.menuOptions = ['Start Game', 'High Scores', 'Settings'];
    this.selectedIndex = 0;

    // Animated starfield
    this.stars = [];
    this._initStars(150);

    // Animated missile trails for background ambiance
    this.trails = [];
    this.trailSpawnTimer = 0;

    // Title animation
    this.titleGlowPhase = 0;
  }

  _initStars(count) {
    this.stars = [];
    for (let i = 0; i < count; i++) {
      this.stars.push({
        x: Math.random(),
        y: Math.random(),
        size: Math.random() * 2 + 0.5,
        speed: Math.random() * 0.02 + 0.005,
        brightness: Math.random() * 0.7 + 0.3,
        twinkleSpeed: Math.random() * 3 + 1,
        twinklePhase: Math.random() * Math.PI * 2,
      });
    }
  }

  _spawnTrail() {
    // Random background missile trail (decorative)
    const fromLeft = Math.random() > 0.5;
    this.trails.push({
      x: fromLeft ? -10 : this.game.width + 10,
      y: Math.random() * this.game.height * 0.3,
      vx: fromLeft ? (Math.random() * 60 + 30) : -(Math.random() * 60 + 30),
      vy: Math.random() * 40 + 20,
      life: 1.0,
      decay: Math.random() * 0.3 + 0.2,
      color: Math.random() > 0.5 ? '#ff3333' : '#ff6600',
      trail: [],
    });
  }

  enter(data = {}) {
    this.selectedIndex = 0;
    this.titleGlowPhase = 0;
    this.trails = [];
    this.trailSpawnTimer = 0;
    // Re-init stars if dimensions changed
    if (this.stars.length === 0) {
      this._initStars(150);
    }

    // Update UI overlay for Playwright testing
    this._updateOverlay();
  }

  exit() {
    // Clear overlay
    if (this.game.uiOverlay) {
      this.game.uiOverlay.innerHTML = '';
    }
  }

  update(dt) {
    const input = this.game.input;

    // Animate starfield
    for (const star of this.stars) {
      star.y += star.speed * dt;
      star.twinklePhase += star.twinkleSpeed * dt;
      if (star.y > 1) {
        star.y = 0;
        star.x = Math.random();
      }
    }

    // Animate missile trails
    this.trailSpawnTimer += dt;
    if (this.trailSpawnTimer > 2.5) {
      this._spawnTrail();
      this.trailSpawnTimer = 0;
    }

    for (let i = this.trails.length - 1; i >= 0; i--) {
      const t = this.trails[i];
      t.trail.push({ x: t.x, y: t.y, alpha: t.life });
      t.x += t.vx * dt;
      t.y += t.vy * dt;
      t.life -= t.decay * dt;
      // Keep trail length manageable
      if (t.trail.length > 30) t.trail.shift();
      if (t.life <= 0 || t.y > this.game.height || t.x < -50 || t.x > this.game.width + 50) {
        this.trails.splice(i, 1);
      }
    }

    // Animate title glow
    this.titleGlowPhase += dt * 2;

    // Keyboard navigation
    if (input.isKeyJustPressed('ArrowDown') || input.isKeyJustPressed('KeyS')) {
      this.selectedIndex = (this.selectedIndex + 1) % this.menuOptions.length;
      this._updateOverlay();
    }
    if (input.isKeyJustPressed('ArrowUp') || input.isKeyJustPressed('KeyW')) {
      this.selectedIndex = (this.selectedIndex - 1 + this.menuOptions.length) % this.menuOptions.length;
      this._updateOverlay();
    }
    if (input.isKeyJustPressed('Enter') || input.isKeyJustPressed('Space')) {
      this._selectOption(this.selectedIndex);
    }

    // Mouse/touch click handling
    if (input.wasClicked()) {
      const pos = input.getPointerPosition();
      const clickedIndex = this._getOptionAtPosition(pos.x, pos.y);
      if (clickedIndex !== -1) {
        this._selectOption(clickedIndex);
      }
    }

    // Tab key for focus cycling
    if (input.isKeyJustPressed('Tab')) {
      this.selectedIndex = (this.selectedIndex + 1) % this.menuOptions.length;
      this._updateOverlay();
    }
  }

  _selectOption(index) {
    // Resume audio context on first user interaction (browser autoplay policy)
    if (this.game.audio && this.game.audio.context) {
      this.game.audio.resume();
    }

    switch (index) {
      case 0: // Start Game
        this.game.state.setState('playing');
        break;
      case 1: // High Scores
        this.game.state.setState('highScores');
        break;
      case 2: // Settings
        this.game.state.setState('settings');
        break;
    }
  }

  _getOptionAtPosition(x, y) {
    const w = this.game.width;
    const h = this.game.height;
    const startY = h * 0.5;
    const spacing = h * 0.08;
    const hitWidth = Math.max(200, w * 0.4); // Min 200px for touch
    const hitHeight = Math.max(44, spacing * 0.8); // Min 44px for touch targets

    for (let i = 0; i < this.menuOptions.length; i++) {
      const optionY = startY + i * spacing;
      const left = w / 2 - hitWidth / 2;
      const top = optionY - hitHeight / 2;

      if (x >= left && x <= left + hitWidth && y >= top && y <= top + hitHeight) {
        return i;
      }
    }
    return -1;
  }

  /**
   * Update the DOM overlay with title screen content for Playwright testing
   */
  _updateOverlay() {
    if (!this.game.uiOverlay) return;

    const menuHtml = this.menuOptions.map((opt, i) => {
      const selected = i === this.selectedIndex ? ' data-selected="true"' : '';
      return `<div class="menu-option" data-index="${i}"${selected}>${opt}</div>`;
    }).join('');

    this.game.uiOverlay.innerHTML = `
      <div data-screen="title">
        <div class="game-title">MISSILE COMMAND</div>
        <div class="menu-options">${menuHtml}</div>
      </div>
    `;
  }

  render(ctx) {
    const w = this.game.width;
    const h = this.game.height;

    // Background gradient
    this.game.renderer.clearBackground();

    // Animated starfield
    this._renderStarfield(ctx, w, h);

    // Background missile trails
    this._renderTrails(ctx);

    // Title text with animated glow
    const glowPulse = 15 + Math.sin(this.titleGlowPhase) * 8;
    this.game.renderer.drawGlowText('MISSILE COMMAND', w / 2, h * 0.25, {
      font: `${Math.max(32, w * 0.04)}px "Share Tech Mono", monospace`,
      color: '#ffffff',
      glowColor: '#00ffff',
      glowSize: glowPulse,
    });

    // Subtitle
    this.game.renderer.drawGlowText('DEFEND YOUR CITIES', w / 2, h * 0.34, {
      font: `${Math.max(12, w * 0.015)}px "Share Tech Mono", monospace`,
      color: '#00ffff',
      glowColor: '#0088ff',
      glowSize: 5,
    });

    // Menu options
    const startY = h * 0.5;
    const spacing = h * 0.08;

    this.menuOptions.forEach((option, i) => {
      const isSelected = i === this.selectedIndex;

      // Selection indicator
      if (isSelected) {
        const indicatorX = w / 2 - w * 0.12;
        this.game.renderer.drawGlowText('\u25B6', indicatorX, startY + i * spacing, {
          font: `${Math.max(14, w * 0.018)}px "Share Tech Mono", monospace`,
          color: '#00ffff',
          glowColor: '#00ffff',
          glowSize: 10,
        });
      }

      this.game.renderer.drawGlowText(option, w / 2, startY + i * spacing, {
        font: `${Math.max(18, w * 0.025)}px "Share Tech Mono", monospace`,
        color: isSelected ? '#00ffff' : '#aabbcc',
        glowColor: isSelected ? '#00ffff' : '#445566',
        glowSize: isSelected ? 15 : 3,
      });
    });

    // Footer instruction
    // Use touch-friendly instruction text
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const footerText = isTouchDevice ? 'TAP TO SELECT' : 'USE ARROW KEYS OR CLICK TO SELECT';
    this.game.renderer.drawGlowText(footerText, w / 2, h * 0.88, {
      font: `${Math.max(10, w * 0.012)}px "Share Tech Mono", monospace`,
      color: '#8899aa',
      glowColor: '#445566',
      glowSize: 2,
    });
  }

  _renderStarfield(ctx, w, h) {
    for (const star of this.stars) {
      const twinkle = 0.5 + 0.5 * Math.sin(star.twinklePhase);
      const alpha = star.brightness * twinkle;

      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(star.x * w, star.y * h, star.size, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  }

  _renderTrails(ctx) {
    for (const t of this.trails) {
      // Draw trail segments
      if (t.trail.length > 1) {
        for (let i = 1; i < t.trail.length; i++) {
          const seg = t.trail[i];
          const prev = t.trail[i - 1];
          const trailAlpha = (i / t.trail.length) * t.life * 0.4;

          ctx.save();
          ctx.globalAlpha = trailAlpha;
          ctx.strokeStyle = t.color;
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.moveTo(prev.x, prev.y);
          ctx.lineTo(seg.x, seg.y);
          ctx.stroke();
          ctx.restore();
        }
      }

      // Draw missile head
      if (t.life > 0.1) {
        ctx.save();
        ctx.globalAlpha = t.life * 0.6;
        ctx.fillStyle = t.color;
        ctx.shadowColor = t.color;
        ctx.shadowBlur = 4;
        ctx.beginPath();
        ctx.arc(t.x, t.y, 2, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
    }
  }
}
